import { FC, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Flex,
	Input,
	Row,
	Select,
	Space,
	Table,
	message,
	Popconfirm,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getIndustrySectorList,
	deleteIndustrySector,
	IndustrySector,
	industrySectorKeys,
} from '@/libs/fleet-api/services/admin-industry-sector';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import IndustrySectorFormModal from '@/pages/admin/modals/IndustrySectorFormModal';

const { Search } = Input;
const { Option } = Select;

const IndustrySectorListPage: FC = () => {
	const queryClient = useQueryClient();
	const [searchText, setSearchText] = useState('');
	const [searchBy, setSearchBy] = useState<'name' | 'code'>('name');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'update'>('create');
	const [selectedSector, setSelectedSector] = useState<
		IndustrySector | undefined
	>(undefined);

	const { data: sectorData, isLoading } = useQuery({
		queryKey: industrySectorKeys.list({
			pagination: { pageNumber: currentPage, pageSize: pageSize },
			search: { by: searchBy, value: searchText },
		}),
		queryFn: () =>
			getIndustrySectorList({
				pagination: { pageNumber: currentPage, pageSize: pageSize },
				search: { by: searchBy, value: searchText },
			}),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteIndustrySector,
		onSuccess: () => {
			message.success('Industry Sector deleted successfully');
			queryClient.invalidateQueries({ queryKey: industrySectorKeys.all });
		},
		onError: (error) => {
			message.error('Failed to delete Industry Sector');
			console.error(error);
		},
	});

	const handleCreate = () => {
		setModalMode('create');
		setSelectedSector(undefined);
		setIsModalOpen(true);
	};

	const handleEdit = (record: IndustrySector) => {
		setModalMode('update');
		setSelectedSector(record);
		setIsModalOpen(true);
	};

	const handleDelete = (id: number) => {
		deleteMutation.mutate(id);
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 60,
		},
		{
			title: 'Industry Class',
			dataIndex: 'industryClass',
			key: 'industryClass',
		},
		{
			title: 'Ecosystem',
			dataIndex: 'ecosystem',
			key: 'ecosystem',
		},
		{
			title: 'Sector Levels',
			key: 'sectorLevels',
			render: (_: unknown, record: IndustrySector) => (
				<div style={{ fontSize: '12px' }}>
					<div>L1: {record.sectorLevel1}</div>
					<div>L2: {record.sectorLevel2}</div>
					{record.sectorLevel3 && <div>L3: {record.sectorLevel3}</div>}
				</div>
			),
		},
		{
			title: 'Code',
			dataIndex: 'code',
			key: 'code',
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right' as const,
			width: 150,
			render: (_: unknown, record: IndustrySector) => (
				<Space>
					<Button
						type="link"
						icon={<EditOutlined />}
						onClick={() => handleEdit(record)}
					>
						Edit
					</Button>
					<Popconfirm
						title="Are you sure you want to delete this Industry Sector?"
						onConfirm={() => handleDelete(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button
							type="link"
							danger
							icon={<DeleteOutlined />}
						>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div style={{ width: '100%' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div style={{ width: '100%' }}>
					<FleetTitle level={1}>
						<Flex
							style={{ gap: 10 }}
							justify="space-between"
							align="center"
						>
							<span>Master Industry Sector</span>
							<Button
								type="primary"
								size="small"
								icon={<PlusOutlined />}
								onClick={handleCreate}
							>
								Add Industry Sector
							</Button>
						</Flex>
					</FleetTitle>
				</div>
			</div>

			<Card>
				<Row
					gutter={[16, 16]}
					style={{ marginBottom: '16px' }}
				>
					<Col
						xs={24}
						sm={8}
					>
						<Select
							value={searchBy}
							onChange={setSearchBy}
							style={{ width: '100%' }}
						>
							<Option value="name">Search by Name (Class)</Option>
							<Option value="code">Search by Code</Option>
						</Select>
					</Col>
					<Col
						xs={24}
						sm={16}
					>
						<Search
							placeholder="Search..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							onSearch={() => setCurrentPage(1)}
							allowClear
							enterButton
						/>
					</Col>
				</Row>

				<Table
					columns={columns}
					dataSource={sectorData?.data?.items || []}
					loading={isLoading}
					rowKey="id"
					pagination={{
						current: currentPage,
						pageSize: pageSize,
						total: sectorData?.data?.totalCount,
						onChange: (page, size) => {
							setCurrentPage(page);
							setPageSize(size);
						},
						showSizeChanger: true,
					}}
					scroll={{ x: 'max-content' }}
				/>
			</Card>

			<IndustrySectorFormModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				mode={modalMode}
				initialValues={selectedSector}
			/>
		</div>
	);
};

export default IndustrySectorListPage;
